import {Grid, Stack} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ActivePowerInvertersChart from "../ActivePower/ActivePowerInvertersChart";
import {useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import {ACard, AMultiSelectFilter, AMultiSelectOption, AMultiSelectOptionId, ASectionTitle, Skeleton} from "@atiautomacao/ati-ui-library";
import {QueryClient, QueryClientProvider} from "react-query";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import {POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";
import useInterval from "../../../Shared/Hooks/useInterval";


export default function DashboardOverviewAtivePowerPage(){
    const {entities} = useAppSelector((state: RootState) => state.dashboardNavigationMenu);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);

    // dataFound tracks, for each power station id, the last cycle in which its
    // request has finished. cycle 0 = initial mount, then +1 each auto-refresh.
    const [dataFound, setDataFound] = useState<Map<number, number>>(new Map());
    const [refreshCycle, setRefreshCycle] = useState(0);
    // Per-card refreshTrigger: bumped when it becomes this card's turn to refetch.
    const [refreshTriggers, setRefreshTriggers] = useState<Map<number, number>>(new Map());
    const isRefreshingRef = useRef(false);

    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    }), []);

    const handleDataFound = useCallback((id: number) => {
        if (id) {
            setDataFound((prev) => {
                const next = new Map(prev);
                next.set(id, refreshCycle);
                return next;
            });
        }
    }, [refreshCycle]);

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...entities]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map(e => ({ id: e.id, label: e.name })),
        [entities]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map(o => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) return;
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const filteredEntities = useMemo(
        () => {
            if (!isInitialized) return entities;
            const selectedSet = new Set(selectedPowerStationIds.map(id => Number(id)));
            return entities.filter(e => selectedSet.has(e.id));
        },
        [isInitialized, selectedPowerStationIds, entities]
    );

    // Card is "done" for the current cycle when its finished cycle >= current cycle.
    const isCardDoneForCycle = useCallback((id: number) => {
        const finishedCycle = dataFound.get(id);
        return finishedCycle !== undefined && finishedCycle >= refreshCycle;
    }, [dataFound, refreshCycle]);

    // Sequential refresh driver: once a card finishes, trigger the next one.
    // Only runs during a refresh cycle (cycle > 0); the initial mount is driven
    // by the render guard that mounts the next card when the previous is done.
    useEffect(() => {
        if (refreshCycle === 0) return;
        if (!filteredEntities || filteredEntities.length === 0) return;

        const nextIdx = filteredEntities.findIndex((e) => !isCardDoneForCycle(e.id));
        if (nextIdx === -1) {
            // All cards finished this cycle.
            isRefreshingRef.current = false;
            return;
        }
        const nextId = filteredEntities[nextIdx].id;
        setRefreshTriggers((prev) => {
            const currentTrigger = prev.get(nextId) ?? 0;
            const desiredTrigger = refreshCycle;
            if (currentTrigger >= desiredTrigger) return prev;
            const next = new Map(prev);
            next.set(nextId, desiredTrigger);
            return next;
        });
    }, [refreshCycle, dataFound, filteredEntities, isCardDoneForCycle]);

    // Kick off a new refresh cycle every mainUpdateInterval (default from user config).
    useInterval(() => {
        if (isRefreshingRef.current) return; // still processing previous cycle
        if (!filteredEntities || filteredEntities.length === 0) return;
        isRefreshingRef.current = true;
        setRefreshCycle((c) => c + 1);
    }, userConfig.mainUpdateInterval);


    return(
        <QueryClientProvider client={queryClient}>
             <Grid
                container
                spacing={3}
                paddingTop={0}
                paddingLeft={'24px'}
                paddingRight={'24px'}
                paddingBottom={'24px'}
                sx={{
                    '& > .MuiGrid-item:first-of-type': {
                        paddingTop: '0 !important'
                    }
                }}
            >
                <Grid item xs={12}>
                    <ASectionTitle titleLabel="Potência Ativa"
                    contentChildren={
                        <Stack direction="row" sx={{ flexShrink: 0, alignItems: "center" }}>
                            <AMultiSelectFilter
                                options={powerStationOptions}
                                selectedIds={selectedPowerStationIds}
                                onApply={handleApplyPowerStationFilter}
                                triggerLabel="Usinas"
                                searchPlaceholder="Buscar usina..."
                            />
                        </Stack>
                    }
                    />
                </Grid>
           
                <>
                    {filteredEntities && filteredEntities.length > 0 ?
                        filteredEntities.map((data, index) => {
                            // Initial mount: mount card once the previous one has finished (cycle 0).
                            // On refresh cycles the card is already mounted; refreshTrigger drives refetch.
                            const previousDone = index === 0 || dataFound.has(filteredEntities[index - 1].id);
                            return (
                                <Grid item xs={12} md={6} lg={4} xl={4} key={data.id}>
                                    {previousDone ?
                                        <ActivePowerInvertersChart
                                            onDataFound={handleDataFound}
                                            powerStationData={data}
                                            refreshTrigger={refreshTriggers.get(data.id)}
                                        />
                                        :
                                        <Skeleton animation="wave" height={350} variant="rounded" width={"100%"}/>
                                    }
                                </Grid>
                            );
                        }) :
                        <Grid item xs={12}>
                            <ACard>
                                Dados não encontrados
                            </ACard>
                        </Grid>
                    }
                </>
            </Grid>
        </QueryClientProvider>
    );
}